import { useAuth0 } from '@auth0/auth0-react'
import { Trans } from '@lingui/macro'
import MaterialSymbol from '@om1/platform-components/MaterialSymbol'
import { toPath } from '@om1/platform-routing'
import { jwtDecode } from 'jwt-decode'
import { CheckSquare, ClipboardList, HeartHandshake, Home, Snowflake, ToggleRight, UserCog, Users } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { Redirect, Route, BrowserRouter as Router, Switch } from 'react-router-dom'
import NavBar from '../components/NavBar'
import { PermissionProtectedRoute } from '../components/PermissionProtectedRoute'
import { AspenRoute, AspenRoutes } from '../routes'
import { AspenPermissions } from '../utils'
import AccessDeniedPage from './AccessDeniedPage'
import CandidateDetailsPage from './CandidateDetailsPage'
import CandidatesPage from './CandidatesPage'
import CarePartnerDetailsPage from './CarePartnerDetailsPage'
import CarePartnersPage from './CarePartnersPage'
import DashboardPage from './DashboardPage'
import GreenlightConfigPage from './GreenlightConfigPage'
import NotFoundPage from './NotFoundPage'
import PatientDetailPage from './PatientDetailPage'
import PatientsPage from './PatientsPage'
import ProviderDetailPage from './ProviderDetailPage'
import ProvidersPage from './ProvidersPage'
import RequestsPage from './RequestsPage'
import SnowflakePage from './SnowflakePage'
import TaskDetailPage from './TaskDetailPage'
import TasksPage from './TasksPage'

export const routeConfig: AspenRoute[] = [
    {
        route: AspenRoutes.DASHBOARD,
        component: DashboardPage,
        permissions: [AspenPermissions.ACCESS],
        name: <Trans>Dashboard</Trans>,
        icon: <Home size={20} className='flex-shrink-0' />
    },
    {
        route: AspenRoutes.CANDIDATES + '/:id',
        component: CandidateDetailsPage,
        permissions: [AspenPermissions.MANAGE_CANDIDATES]
    },
    {
        route: AspenRoutes.CANDIDATES,
        component: CandidatesPage,
        permissions: [AspenPermissions.MANAGE_CANDIDATES],
        name: <Trans>Candidates</Trans>,
        icon: <MaterialSymbol name='person_raised_hand' size={24} />
    },
    {
        route: AspenRoutes.PATIENTS + '/:id',
        component: PatientDetailPage,
        permissions: [AspenPermissions.ACCESS]
    },
    {
        route: AspenRoutes.PATIENTS,
        component: PatientsPage,
        permissions: [AspenPermissions.ACCESS],
        name: <Trans>Patients</Trans>,
        icon: <Users size={20} className='flex-shrink-0' />
    },
    {
        route: AspenRoutes.CAREPARTNERS + '/:id',
        component: CarePartnerDetailsPage,
        permissions: [AspenPermissions.ACCESS]
    },
    {
        route: AspenRoutes.CAREPARTNERS,
        component: CarePartnersPage,
        permissions: [AspenPermissions.ACCESS],
        name: <Trans>Care Partners</Trans>,
        icon: <HeartHandshake size={20} className='flex-shrink-0' />
    },
    {
        route: AspenRoutes.PROVIDERS + '/:id',
        component: ProviderDetailPage,
        permissions: [AspenPermissions.ACCESS]
    },
    {
        route: AspenRoutes.PROVIDERS,
        component: ProvidersPage,
        permissions: [AspenPermissions.ACCESS],
        name: <Trans>Providers</Trans>,
        icon: <UserCog size={20} className='flex-shrink-0' />
    },
    {
        route: AspenRoutes.REQUESTS,
        component: RequestsPage,
        permissions: [AspenPermissions.ACCESS],
        name: <Trans>Requests</Trans>,
        icon: <ClipboardList size={20} className='flex-shrink-0' />
    },
    {
        route: AspenRoutes.TASKS + '/:id',
        component: TaskDetailPage,
        permissions: [AspenPermissions.ACCESS]
    },
    {
        route: AspenRoutes.TASKS,
        component: TasksPage,
        permissions: [AspenPermissions.ACCESS],
        name: <Trans>Tasks</Trans>,
        icon: <CheckSquare size={20} className='flex-shrink-0' />
    },
    {
        route: AspenRoutes.SNOWFLAKE,
        component: SnowflakePage,
        permissions: [AspenPermissions.ADMIN],
        name: <Trans>Snowflake</Trans>,
        icon: <Snowflake size={20} className='flex-shrink-0' />
    },
    {
        route: AspenRoutes.GREENLIGHT,
        component: GreenlightConfigPage,
        permissions: [AspenPermissions.ADMIN],
        name: <Trans>Greenlight</Trans>,
        icon: <ToggleRight size={20} className='flex-shrink-0' />
    },
    {
        route: AspenRoutes.NOTFOUND,
        component: NotFoundPage,
        permissions: [],
        name: <Trans>Not Found</Trans>
    },
    {
        route: AspenRoutes.ACCESS_DENIED,
        component: AccessDeniedPage,
        permissions: [],
        name: <Trans>Access Denied</Trans>
    }
]

const LandingPageComponent: React.FunctionComponent = () => {
    const { getAccessTokenSilently } = useAuth0()
    const [permissions, setPermissions] = useState<string[]>([])
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        getAccessTokenSilently().then((token) => {
            const decodedToken = jwtDecode<{ permissions: string[] }>(String(token))
            setPermissions(decodedToken.permissions || [])
            setIsLoading(false)
        })
    }, [getAccessTokenSilently])

    if (isLoading) {
        return <></>
    }

    const canAccessManage = [AspenPermissions.ACCESS].every((permission) => permissions.includes(permission))
    const canAccessAdmin = [AspenPermissions.ADMIN].every((permission) => permissions.includes(permission))

    const redirectPath = canAccessManage ? AspenRoutes.DASHBOARD : canAccessAdmin ? AspenRoutes.SNOWFLAKE : AspenRoutes.ACCESS_DENIED

    let routes = routeConfig.map((value: AspenRoute) => {
        const { route, component, exact } = value
        return (
            <Route exact={exact} key={route} path={toPath(route)}>
                <PermissionProtectedRoute
                    key={route}
                    requiredPermissions={value.permissions}
                    accessDeniedComponent={<AccessDeniedPage />}
                    protectedChild={React.createElement(component)}
                />
            </Route>
        )
    })

    return (
        <div className='flex flex-col min-h-screen bg-gray-100 flex-1 fixed w-full h-full'>
            <Router>
                <div className='flex flex-col h-full'>
                    <NavBar />
                    <main className='mt-16 ml-16'>
                        <Switch>
                            {routes}
                            <Redirect key={'homeRedirect'} exact from={'/'} to={redirectPath ?? AspenRoutes.ACCESS_DENIED} />
                            <Redirect key={'notFoundRedirect'} from={'*'} to={toPath(AspenRoutes.NOTFOUND)} />
                        </Switch>
                    </main>
                </div>
            </Router>
            <footer className='bg-primary-main text-white text-center z-50'>
                <p>
                    {new Date().getFullYear()} &copy; <Trans>Copyright OM1, All rights reserved.</Trans>
                </p>
            </footer>
        </div>
    )
}

export default LandingPageComponent
